// @ts-expect-error - TS7016 - Could not find a declaration file for module 'atob-lite'. '/buildeng/bamboo-agent-home/xml-data/build-dir/JF-TSMIG123-APPLY/node_modules/atob-lite/atob-node.js' implicitly has an 'any' type.
import atob from 'atob-lite';
import { getAriConfig, toAri, type Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import type { Environment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	SERVICE_PROJECT_ASSOCIATION_TYPES,
	OPSGENIE_STAGING_URL,
	OPSGENIE_PROD_URL,
} from './constants.tsx';
import type {
	DevOpsService,
	DevOpsServiceJiraProjectAssociation,
	MutationErrorAGG,
} from './types.tsx';

export const mapAggMutationResultToSwag = (
	errors?: ReadonlyArray<MutationErrorAGG> | null,
): {
	statusCode: number;
	message: string;
	errorType: string | null;
} => ({
	statusCode: (errors ?? [])[0]?.extensions?.statusCode ?? 0,
	errorType: (errors ?? [])[0]?.extensions?.errorType ?? null,
	message: (errors ?? [])[0]?.message ?? '',
});

// SSR-compatible
const ssrSafeBtoa = (str: string): string => {
	if (typeof window !== 'undefined' && window.btoa) {
		return window.btoa(str);
	}
	return Buffer.from(str).toString('base64');
};

export const mapServiceAriToPublicId = (serviceAri: string): string => {
	if (fg('ssring_affected_services_in_issue_view')) {
		return `b:${ssrSafeBtoa(serviceAri)}`;
	}
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return `b:${window.btoa(serviceAri)}`;
};

export const serviceAriToServiceUrlParam = (serviceAri: string): string =>
	encodeURIComponent(
		serviceAri.startsWith('b:') ? serviceAri : mapServiceAriToPublicId(serviceAri),
	);

export const serviceUrlParamToServiceAri = (encoded: string): string =>
	atob(decodeURIComponent(encoded).slice(2));

export const getChangeManagementAssociation = (
	service: DevOpsService,
): DevOpsServiceJiraProjectAssociation | null => {
	const changeManagementAssociation =
		service.jiraProjects &&
		service.jiraProjects.nodes.find(
			(assoc) => assoc.associationType === SERVICE_PROJECT_ASSOCIATION_TYPES.CHANGE_MANAGEMENT,
		);
	return changeManagementAssociation || null;
};

export const getOpsgenieBaseUrl = (environment: Environment | null) =>
	environment && environment === 'staging' ? OPSGENIE_STAGING_URL : OPSGENIE_PROD_URL;

export const getOgTeamUrl = (href: string, environment: Environment | null) => {
	if (!href.startsWith('http')) {
		const ogBaseUrl = getOpsgenieBaseUrl(environment);
		return `${ogBaseUrl}/${href}`;
	}
	return href;
};

// Returns hrefs in the form `teams/dashboard/{teamId}/main` based on input that's either a team ID (UUID) or team ARI
export const getOpsgenieTeamHref = (ogTeamIdOrAri?: string): string | undefined => {
	if (ogTeamIdOrAri == null) {
		return undefined;
	}

	let teamId: string | null = null;
	if (/^[a-z0-9-]{36}$/.test(ogTeamIdOrAri)) {
		// ogTeamIdOrAri === {teamId}
		teamId = ogTeamIdOrAri;
	} else {
		const teamAri: Ari | null = toAri(ogTeamIdOrAri) ?? null;
		// ogTeamIdOrAri === ari:cloud:opsgenie:{cloudId}:team/{teamId}
		if (teamAri != null) {
			const { resourceOwner, resourceType, resourceId } = getAriConfig(teamAri);
			if (resourceOwner === 'opsgenie' && resourceType === 'team') {
				teamId = resourceId;
			}
		}
	}
	return teamId == null ? undefined : `teams/dashboard/${teamId}/main`;
};
